import { t } from "i18next";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { Eye, RotateCcw } from "lucide-react";

import { FLASHPAY_ROLES, hasAnyRole } from "@/core/auth";
import { RowActionsMenu } from "@/core/components/shared/data-table";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { JournalEntryReverseDialog } from "@/features/ledger/components/journal-entries/journal-entry-reverse-dialog";
import type { JournalEntryRow } from "@/features/ledger/components/journal-entries/journal-entries-columns";
import { useSubmitApprovalRequest } from "@/features/shared/api/use-submit-approval-request";

/**
 * Menu d'actions de ligne du journal comptable.
 * La contre-passation est une action protégée (guide §5.4) réservée à
 * flashpay-superadmin / flashpay-admin (masquage — le backend reste
 * l'autorité finale) et limitée aux écritures POSTED : le dialog crée une
 * demande d'approbation LEDGER_MANUAL_REVERSAL (maker-checker) — la
 * contre-passation réelle (`/reverse`) est exécutée par l'approbateur
 * depuis la page détail de la demande (`/app/admin/approvals/{id}`).
 */
export function JournalEntryRowActions({
  entry,
  navigate,
}: {
  entry: JournalEntryRow;
  navigate: NavigateFunction;
}) {
  const submitApproval = useSubmitApprovalRequest();
  const [reverseOpen, setReverseOpen] = useState(false);

  const canReverse =
    hasAnyRole([FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]) &&
    entry.status === "POSTED";

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            id: "view",
            label: t("ledger.journal.actions.view"),
            icon: Eye,
            onClick: () => navigate(`/app/ledger/journal/${entry.id}`),
          },
          ...(canReverse
            ? [
                {
                  id: "reverse",
                  label: t("ledger.journal.actions.reverse"),
                  icon: RotateCcw,
                  variant: "destructive" as const,
                  onClick: () => setReverseOpen(true),
                },
              ]
            : []),
        ]}
      />

      {canReverse ? (
        <JournalEntryReverseDialog
          entry={entry}
          open={reverseOpen}
          onOpenChange={setReverseOpen}
          onConfirm={(reason) =>
            submitApproval.mutate(
              {
                actionType: "LEDGER_MANUAL_REVERSAL",
                sourceService: "ledger-service",
                resourceType: "JournalEntry",
                resourceId: entry.id,
                payload: {
                  reference: entry.reference,
                  sourceTransactionRef: entry.sourceTransactionRef,
                  productCode: entry.productCode,
                },
                reason,
              },
              {
                onSuccess: () => {
                  notifSilence(t("ledger.journal.feedback.approvalRequested"));
                  setReverseOpen(false);
                  navigate("/app/admin/approvals");
                },
                onError: (error) => notifSilenceError(error.message),
              }
            )
          }
          pending={submitApproval.isPending}
        />
      ) : null}
    </>
  );
}
